import React from 'react';
import { motion } from 'framer-motion';
import { Star, Play, Quote, MapPin } from 'lucide-react';

/**
 * Video Testimonials — 3 premium social-proof cards.
 *
 * Design intent: Apple-style testimonial trio with typographic Devanagari
 * quotes, subtle play-button, star rating, client name + city.
 *
 * NOTE: The play button is currently decorative (no live video URL). When
 * Guruji supplies MP4/YouTube URLs, wire the click handler to open a modal.
 * Thumbnail uses a color-gradient placeholder for now — swap `thumbnail`
 * field to a real image URL when videos are ready.
 */
const TESTIMONIALS = [
  {
    name: 'रीना शर्मा',
    city: 'Faridabad · Haryana',
    rating: 5,
    quote:
      'Acharya Ji ने विवाह की सटीक अवधि बताई। मेरी शादी उसी महीने तय हुई जो उन्होंने कहा था। शास्त्रीय विश्लेषण अद्भुत है।',
    gradient: 'from-[#5a1a2e] via-[#3d101e] to-[#0A0A0A]',
    initials: 'रश',
  },
  {
    name: 'अमित गुप्ता',
    city: 'Delhi NCR',
    rating: 5,
    quote:
      'व्यवसाय के लिए सही timing मिली। ग्रहों की स्थिति के आधार पर जो सलाह मिली, वह पूरी तरह सटीक निकली। धन्यवाद Acharya Ji।',
    gradient: 'from-[#4A1F0A] via-[#5a1a2e] to-[#0A0A0A]',
    initials: 'अगु',
  },
  {
    name: 'Priya Malhotra',
    city: 'Mumbai',
    rating: 5,
    quote:
      'बहुत Professional और शास्त्रीय Consultation। ₹99 में जो analysis मिला वो market में हज़ारों में नहीं मिलता। Highly recommended।',
    gradient: 'from-[#3d101e] via-[#2c0b14] to-[#0A0A0A]',
    initials: 'PM',
  },
];

export default function VideoTestimonials() {
  const easeLux = [0.22, 1, 0.36, 1];

  return (
    <section
      className="relative py-16 lg:py-24"
      data-testid="video-testimonials"
    >
      {/* Ambient royal-purple bloom */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[#3d101e]/22 blur-[200px] pointer-events-none" />

      <div className="container-custom max-w-6xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: easeLux }}
          className="text-center mb-16 lg:mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
            <span className="text-[10px] uppercase tracking-[0.45em] text-[#D4AF37] font-medium">
              33,000+ Client अनुभव
            </span>
            <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
          </div>
          <h2 className="font-heading text-[36px] sm:text-5xl lg:text-[56px] leading-[1.05] font-light text-[#F5F5F5] tracking-[-0.02em] mb-6">
            असली <span className="text-gold-gradient font-normal">यजमानों</span> की
            <br />
            आवाज़ में
          </h2>
          <p className="text-[#B5B5B5] text-[15px] sm:text-[17px] font-light max-w-lg mx-auto leading-[1.85]">
            Acharya Vivek Ji के मार्गदर्शन से जीवन में सकारात्मक बदलाव।
          </p>

          {/* Lotus divider */}
          <div className="relative flex items-center justify-center mt-12 max-w-md mx-auto">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
            <div className="mx-4 relative">
              <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
              <div className="absolute inset-0 w-2 h-2 rotate-45 bg-[#D4AF37] blur-sm opacity-70" />
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/35 to-transparent" />
          </div>
        </motion.div>

        {/* Testimonial trio */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={t.name} testimonial={t} delay={i * 0.12} />
          ))}
        </div>

        {/* Aggregate rating strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: easeLux, delay: 0.4 }}
          className="mt-14 lg:mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-[13px] text-[#B5B5B5]"
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((n) => (
                <Star key={n} className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.2} />
              ))}
            </div>
            <span className="tracking-wider font-light">
              <span className="text-[#F5F5F5] font-normal">4.9</span> / 5
            </span>
          </div>
          <span className="hidden sm:block w-px h-5 bg-white/10" />
          <span className="tracking-wider font-light">
            <span className="text-[#F5F5F5] font-normal">33,000+</span> verified reviews
          </span>
          <span className="hidden sm:block w-px h-5 bg-white/10" />
          <span className="tracking-wider font-light">
            <span className="text-[#F5F5F5] font-normal">98%</span> repeat consultations
          </span>
        </motion.div>
      </div>
    </section>
  );
}

const TestimonialCard = ({ testimonial: t, delay = 0 }) => {
  const easeLux = [0.22, 1, 0.36, 1];
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1.1, ease: easeLux, delay }}
      className="group relative bg-gradient-to-br from-[#0C0C0C] via-[#0A0A0A] to-[#080808] border border-white/[0.06] rounded-[28px] overflow-hidden hover:border-[#D4AF37]/25 transition-colors duration-700 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] hover:shadow-[0_30px_80px_-30px_rgba(212,175,55,0.14)]"
    >
      {/* Thumbnail area — mock video with premium gradient + play button */}
      <div className={`relative h-40 sm:h-48 bg-gradient-to-br ${t.gradient} overflow-hidden`}>
        {/* Faint yantra ornament */}
        <div className="absolute inset-0 opacity-[0.10] pointer-events-none">
          <svg viewBox="0 0 100 100" className="w-full h-full text-[#D4AF37]">
            <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="0.35" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.35" />
            {Array.from({ length: 8 }).map((_, i) => (
              <line key={i} x1="50" y1="50" x2="50" y2="8" stroke="currentColor" strokeWidth="0.25"
                transform={`rotate(${(i * 360) / 8} 50 50)`} />
            ))}
          </svg>
        </div>

        {/* Initials circle (larger, more premium) */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#050505]/70 to-[#1a0710]/70 backdrop-blur-md border-2 border-[#D4AF37]/35 flex items-center justify-center shadow-[0_10px_40px_-8px_rgba(0,0,0,0.85)] ring-4 ring-[#D4AF37]/8">
            <span className="font-heading text-3xl text-[#D4AF37] font-light tracking-tight">
              {t.initials}
            </span>
          </div>
        </div>

        {/* Play button overlay */}
        <button
          type="button"
          aria-label={`Play testimonial from ${t.name}`}
          className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#D4AF37] hover:bg-[#F3E5AB] flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)] transition-colors duration-500 group-hover:scale-105 group-hover:shadow-[0_14px_40px_-6px_rgba(212,175,55,0.85)]"
          onClick={(e) => e.preventDefault()}
          data-testid={`testimonial-play-${t.initials.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Play className="w-4 h-4 text-black ml-0.5 fill-black" strokeWidth={1.5} />
        </button>

        {/* Duration chip */}
        <div className="absolute bottom-4 left-4 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm border border-white/10 text-[10px] text-[#F5F5F5]/80 tracking-wider font-light">
          0:42
        </div>
      </div>

      {/* Content */}
      <div className="p-7">
        {/* Quote */}
        <div className="relative mb-6">
          <Quote className="absolute -top-1 -left-1 w-4 h-4 text-[#D4AF37]/45" strokeWidth={1.5} />
          <p className="text-[#D5D5D5] text-[14px] leading-[1.85] font-light pl-5">
            {t.quote}
          </p>
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between pt-5 border-t border-white/[0.05]">
          <div>
            <p className="text-[#F5F5F5] text-[13.5px] font-normal tracking-tight">
              {t.name}
            </p>
            <p className="text-[11px] text-[#8A8A8A] flex items-center gap-1 mt-0.5 tracking-wider font-light">
              <MapPin className="w-2.5 h-2.5 text-[#D4AF37]/70" strokeWidth={1.6} />
              {t.city}
            </p>
          </div>
          <div className="flex items-center gap-0.5">
            {Array.from({ length: t.rating }).map((_, n) => (
              <Star key={n} className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.2} />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
